import type { GetFinalizedStoryResponse } from "@api/types";
import { Palette } from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useState } from "react";
import { DrawingCarouselModal } from "./ui/drawing-carousel-modal";
import { StoryText } from "./ui/story-text";

interface StoryViewerProps {
    storyData: GetFinalizedStoryResponse;
    className?: string;
}

export function StoryViewer({ storyData, className = "" }: StoryViewerProps) {
    const [isCarouselOpen, setIsCarouselOpen] = useState(false);
    const [carouselInitialIndex, setCarouselInitialIndex] = useState(0);
    const posthog = usePostHog();

    const sortedSteps = storyData.steps.sort((a, b) => a.step_number - b.step_number);

    // Extract all drawings for the carousel
    const allDrawings = sortedSteps
        .filter((step) => step.drawing?.image_data)
        .map((step) => ({
            image_data: step.drawing!.image_data,
            ai_interpretation: step.drawing!.ai_interpretation,
            drawing_prompt: step.drawing_prompt,
            chapter: step.step_number,
        }));

    const handleDrawingClick = (stepNumber: number) => {
        const drawingIndex = allDrawings.findIndex((drawing) => drawing.chapter === stepNumber);
        if (drawingIndex >= 0) {
            // Track image click for engagement analytics
            posthog.capture("story_image_clicked", {
                story_id: storyData.story.id,
                step_number: stepNumber,
                total_steps: storyData.story.total_steps,
                drawing_index: drawingIndex,
                total_drawings: allDrawings.length,
                timestamp: new Date().toISOString(),
            });

            setCarouselInitialIndex(drawingIndex);
            setIsCarouselOpen(true);
        }
    };

    return (
        <div className={`space-y-8 ${className}`}>
            {/* Story chapters */}
            <div className="prose prose-lg max-w-none space-y-8">
                {sortedSteps.map((step, index) => (
                    <div
                        key={step.id}
                        className="chapter-wrapper animate-fade-in"
                        style={{ animationDelay: `${index * 0.1}s` }}
                    >
                        <StoryChapter
                            step={step}
                            stepNumber={step.step_number}
                            onDrawingClick={() => handleDrawingClick(step.step_number)}
                        />

                        {/* Simple chapter divider */}
                        {index < sortedSteps.length - 1 && (
                            <div className="flex justify-center my-8">
                                <div className="w-24 h-px bg-border"></div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Drawing Carousel Modal */}
            <DrawingCarouselModal
                isOpen={isCarouselOpen}
                onClose={() => setIsCarouselOpen(false)}
                drawings={allDrawings}
                initialIndex={carouselInitialIndex}
            />
        </div>
    );
}

function StoryChapter({
    step,
    stepNumber,
    onDrawingClick,
}: {
    step: {
        id: string;
        story_text: string;
        drawing_prompt: string;
        user_prompt: string;
        story_followup_text?: string;
        drawing?: { image_data: string; ai_interpretation: string };
    };
    stepNumber: number;
    onDrawingClick?: () => void;
}) {
    const handleDrawingClick = () => {
        if (step.drawing?.image_data && onDrawingClick) {
            onDrawingClick();
        }
    };

    return (
        <div key={step.id} className="space-y-6">
            {/* Simple chapter header */}
            <div className="flex items-center gap-2 mb-4">
                <span className="text-sm font-medium text-primary bg-primary/10 px-3 py-1 rounded-full">
                    Chapter {stepNumber}
                </span>
            </div>

            <div className="space-y-6">
                <div className="story-text">
                    <StoryText>{step.story_text}</StoryText>
                </div>

                <div className="drawing-section">
                    <div className="text-center mb-4">
                        <p className="text-sm text-muted-foreground italic mb-4">"{step.drawing_prompt}"</p>
                        <div className="flex justify-center">
                            {step.drawing?.image_data ? (
                                <div
                                    className="drawing-container group cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:shadow-lg"
                                    onClick={handleDrawingClick}
                                >
                                    <div className="relative bg-background rounded-lg border border-border shadow-sm overflow-hidden">
                                        <img
                                            src={step.drawing.image_data}
                                            alt={`Drawing of ${step.drawing_prompt}`}
                                            className="w-full h-48 object-contain p-2"
                                        />
                                        {/* Simple hover indicator */}
                                        <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center">
                                            <div className="page-gradient text-foreground text-sm px-3 py-1 rounded-full shadow-lg border border-border">
                                                Click to zoom
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="drawing-placeholder bg-muted rounded-lg border-2 border-dashed border-muted-foreground/30 w-full h-48 flex items-center justify-center">
                                    <Palette className="h-16 w-16 text-muted-foreground" />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {step.story_followup_text && (
                    <div className="followup-text">
                        <StoryText>{step.story_followup_text}</StoryText>
                    </div>
                )}
            </div>
        </div>
    );
}
